import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './OnboardingTour.css';

const SPOT_PADDING = 6;
const CARD_GAP = 12;
const VIEWPORT_MARGIN = 12;

// Шаг: { target?: CSS-селектор, title, body, optional?: пропускать, если элемента нет на странице,
//        menuItem?: пункт бокового меню — на телефоне меню спрятано под ☰, тогда шаг показываем по центру с подсказкой }
// Один и тот же якорь может быть в нескольких местах (например, «Создать опрос» в боковом меню и,
// на телефоне, над списком) — берём первый видимый на экране.
function isShown(el) {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    if (rect.right <= 0 || rect.left >= window.innerWidth) return false;
    if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
    }
    return window.getComputedStyle(el).visibility !== 'hidden';
}

function findTarget(step) {
    if (!step || !step.target) return null;
    return Array.from(document.querySelectorAll(step.target)).find(isShown) || null;
}

function OnboardingTour({ steps, onClose }) {
    const visibleSteps = steps.filter((step) => !step.optional || step.menuItem || findTarget(step));
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const [cardPos, setCardPos] = useState(null);
    const cardRef = useRef(null);
    const step = visibleSteps[Math.min(index, visibleSteps.length - 1)];
    const isLast = index >= visibleSteps.length - 1;

    const measure = useCallback(() => {
        const el = findTarget(step);
        setRect(el ? el.getBoundingClientRect() : null);
    }, [step]);

    useEffect(() => {
        const el = findTarget(step);
        if (el) el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        measure();
    }, [step, measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [measure]);

    // Карточка подсказки: под элементом, если не помещается — над ним, без элемента — по центру.
    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card) return;
        const { innerWidth: vw, innerHeight: vh } = window;
        const cw = card.offsetWidth;
        const ch = card.offsetHeight;
        if (!rect) {
            setCardPos({ left: Math.max(VIEWPORT_MARGIN, (vw - cw) / 2), top: Math.max(VIEWPORT_MARGIN, (vh - ch) / 2) });
            return;
        }
        const left = Math.min(Math.max(rect.left + rect.width / 2 - cw / 2, VIEWPORT_MARGIN), vw - cw - VIEWPORT_MARGIN);
        let top = rect.bottom + SPOT_PADDING + CARD_GAP;
        if (top + ch > vh - VIEWPORT_MARGIN) {
            const above = rect.top - SPOT_PADDING - CARD_GAP - ch;
            top = above >= VIEWPORT_MARGIN ? above : Math.max(VIEWPORT_MARGIN, vh - ch - VIEWPORT_MARGIN);
        }
        setCardPos({ left, top });
    }, [rect, index]);

    useEffect(() => {
        if (cardRef.current) cardRef.current.focus();
    }, [index]);

    const next = useCallback(() => {
        if (isLast) onClose('done');
        else setIndex((i) => i + 1);
    }, [isLast, onClose]);
    const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose('skip');
            else if (e.key === 'ArrowRight') next();
            else if (e.key === 'ArrowLeft') back();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [next, back, onClose]);

    if (!step) return null;

    const spotStyle = rect ? {
        left: rect.left - SPOT_PADDING,
        top: rect.top - SPOT_PADDING,
        width: rect.width + SPOT_PADDING * 2,
        height: rect.height + SPOT_PADDING * 2,
    } : null;

    return (
        <div className="tour" role="presentation">
            {spotStyle
                ? <div className="tour__spot" style={spotStyle} aria-hidden />
                : <div className="tour__backdrop" aria-hidden />}
            <div
                ref={cardRef}
                className="tour__card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tour-title"
                aria-describedby="tour-body"
                tabIndex={-1}
                style={cardPos ? { left: cardPos.left, top: cardPos.top } : { visibility: 'hidden' }}
            >
                <div className="tour__progress">
                    <span>Шаг {index + 1} из {visibleSteps.length}</span>
                    <button type="button" className="tour__skip" onClick={() => onClose('skip')}>
                        Пропустить обучение
                    </button>
                </div>
                <h2 id="tour-title" className="tour__title">{step.title}</h2>
                <div id="tour-body" className="tour__body">
                    {step.body}
                    {step.menuItem && !rect && <div className="tour__note">Этот пункт — в меню: нажмите ☰ в левом верхнем углу.</div>}
                </div>
                <div className="tour__dots" aria-hidden>
                    {visibleSteps.map((s, i) => (
                        <span key={s.id || i} className={`tour__dot${i === index ? ' tour__dot--active' : ''}`} />
                    ))}
                </div>
                <div className="tour__actions">
                    {index > 0 && (
                        <button type="button" className="btn btn--secondary btn--sm" onClick={back}>Назад</button>
                    )}
                    <button type="button" className="btn btn--primary btn--sm" onClick={next}>
                        {isLast ? 'Готово' : 'Далее'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default OnboardingTour;
